import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "../../components/layout/AdminLayout";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";

export default function RekapPresensi() {
  const { token } = useAuth();

  const [data, setData] = useState([]);
  const [listKelas, setListKelas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const [form, setForm] = useState({
    tipe: "harian",
    id_kelas: "",
    tanggal: new Date().toISOString().split("T")[0],
    start: "",
    end: "",
    bulan: "",
  });

  // ===== GET LIST KELAS =====
  useEffect(() => {
    const fetchKelas = async () => {
      try {
        const res = await axios.get("https://backend.smpn1kamal.sch.id/api/kelas", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setListKelas(res.data.data || res.data);
      } catch {
        toast.error("Gagal mengambil daftar kelas");
      }
    };
    fetchKelas();
  }, [token]);

  // ===== GET DATA =====
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://backend.smpn1kamal.sch.id/api/presensi/rekap-global", {
        params: form,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error("Gagal ambil rekap");
    } finally {
      setLoading(false);
    }
  };

  // ===== DOWNLOAD =====
  const downloadFile = async (format) => {
    try {
      setDownloading(format);

      const url =
        format === "excel"
          ? "https://backend.smpn1kamal.sch.id/api/presensi/rekap-global/excel"
          : "https://backend.smpn1kamal.sch.id/api/presensi/rekap-global/pdf";

      const res = await axios.get(url, {
        params: form,
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Cek error JSON
      const contentType = res.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.error(json.message || `Gagal download ${format.toUpperCase()}`);
        return;
      }

      // ★ Ambil nama file dari header Content-Disposition (server sudah expose via Access-Control-Expose-Headers)
      const ext = format === "excel" ? "xlsx" : "pdf";
      let filename = `rekap-presensi.${ext}`; // fallback

      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";\r\n]+)"?/i);
      if (match?.[1]) filename = match[1].trim();

      // Trigger download
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success(`Berhasil download ${format.toUpperCase()}`);
    } catch (err) {
      console.error("Download error:", err);
      toast.error(`Gagal download ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4">

        {/* ===== HEADER ===== */}
        <div className="space-y-3">
          {/* DESKTOP */}
          <div className="hidden md:flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-indigo-600">Laporan Presensi</h1>
              <p className="text-gray-500">SMPN 1 Kamal</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => downloadFile("excel")}
                disabled={downloading !== null}
                className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-5 py-2 rounded-full transition"
              >
                {downloading === "excel" ? "Mengunduh..." : "Unduh Excel"}
              </button>
              <button
                onClick={() => downloadFile("pdf")}
                disabled={downloading !== null}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-5 py-2 rounded-full transition"
              >
                {downloading === "pdf" ? "Mengunduh..." : "Unduh PDF"}
              </button>
            </div>
          </div>

          {/* MOBILE */}
          <div className="md:hidden space-y-3">
            <div>
              <h1 className="text-2xl font-bold text-indigo-600">Laporan Presensi</h1>
              <p className="text-gray-500">SMPN 1 Kamal</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => downloadFile("excel")}
                disabled={downloading !== null}
                className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-4 py-2 rounded-full text-sm transition"
              >
                {downloading === "excel" ? "..." : "Unduh Excel"}
              </button>
              <button
                onClick={() => downloadFile("pdf")}
                disabled={downloading !== null}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-4 py-2 rounded-full text-sm transition"
              >
                {downloading === "pdf" ? "..." : "Unduh PDF"}
              </button>
            </div>
          </div>
        </div>

        {/* ===== CARD FILTER ===== */}
        <div className="bg-white rounded-2xl shadow-md p-5">
          <div className="flex flex-col md:flex-row gap-3 md:justify-between">
            <div className="flex flex-wrap gap-3">
              <select
                value={form.id_kelas}
                onChange={(e) => setForm({ ...form, id_kelas: e.target.value })}
                className="border rounded-full px-4 py-2 text-sm"
              >
                <option value="">Semua Kelas</option>
                {listKelas.map((k) => (
                  <option key={k.id_kelas} value={k.id_kelas}>{k.nama_kelas}</option>
                ))}
              </select>

              <select
                value={form.tipe}
                onChange={(e) => setForm({ ...form, tipe: e.target.value })}
                className="border rounded-full px-4 py-2 text-sm"
              >
                <option value="harian">Harian</option>
                <option value="range">Range</option>
                <option value="bulanan">Bulanan</option>
              </select>

              {form.tipe === "harian" && (
                <input
                  type="date"
                  value={form.tanggal}
                  onChange={(e) => setForm({ ...form, tanggal: e.target.value })}
                  className="border rounded-full px-4 py-2 text-sm"
                />
              )}
              {form.tipe === "range" && (
                <>
                  <input type="date" value={form.start}
                    onChange={(e) => setForm({ ...form, start: e.target.value })}
                    className="border rounded-full px-4 py-2 text-sm" placeholder="Dari" />
                  <input type="date" value={form.end}
                    onChange={(e) => setForm({ ...form, end: e.target.value })}
                    className="border rounded-full px-4 py-2 text-sm" placeholder="Sampai" />
                </>
              )}
              {form.tipe === "bulanan" && (
                <input type="month" value={form.bulan}
                  onChange={(e) => setForm({ ...form, bulan: e.target.value })}
                  className="border rounded-full px-4 py-2 text-sm" />
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white px-5 py-2 rounded-full transition"
            >
              {loading ? "Loading..." : "Tampilkan"}
            </button>
          </div>
        </div>

        {/* ===== DATA ===== */}
        <div className="bg-white rounded-2xl shadow-md p-5">
          {loading ? (
            <p className="text-center py-6 text-gray-400">Loading...</p>
          ) : (
            <>
              {/* MOBILE */}
              <div className="md:hidden space-y-3">
                {data.length === 0 ? (
                  <p className="text-center text-gray-400">Tidak ada data</p>
                ) : (
                  data.map((d, i) => (
                    <div key={i} className="border rounded-xl p-4 space-y-1">
                      <p className="font-semibold text-sm">{d.tanggal} — {d.hari}</p>
                      <p className="font-semibold">{d.kelas}</p>
                      <p className="text-sm text-gray-500">{d.mapel}</p>
                      <p className="text-sm">{d.guru}</p>
                      <div className="flex gap-2 mt-1 text-xs font-semibold">
                        <span className="text-green-600">H:{d.h}</span>
                        <span className="text-yellow-500">S:{d.s}</span>
                        <span className="text-blue-500">I:{d.i}</span>
                        <span className="text-red-500">A:{d.a}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* DESKTOP */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-center">No</th>
                      <th className="px-4 py-3 text-left">Tanggal</th>
                      <th className="px-4 py-3 text-left">Hari</th>
                      <th className="px-4 py-3 text-left">Kelas</th>
                      <th className="px-4 py-3 text-left">Mapel</th>
                      <th className="px-4 py-3 text-left">Guru</th>
                      <th className="px-4 py-3 text-center text-green-600">H</th>
                      <th className="px-4 py-3 text-center text-yellow-500">S</th>
                      <th className="px-4 py-3 text-center text-blue-500">I</th>
                      <th className="px-4 py-3 text-center text-red-500">A</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-6 text-gray-400">Tidak ada data</td>
                      </tr>
                    ) : (
                      data.map((d, i) => (
                        <tr key={i} className={`border-t ${i % 2 === 0 ? "bg-gray-50" : ""}`}>
                          <td className="px-4 py-3 text-center">{i + 1}</td>
                          <td className="px-4 py-3">{d.tanggal}</td>
                          <td className="px-4 py-3">{d.hari}</td>
                          <td className="px-4 py-3">{d.kelas}</td>
                          <td className="px-4 py-3">{d.mapel}</td>
                          <td className="px-4 py-3">{d.guru}</td>
                          <td className="px-4 py-3 text-center font-semibold text-green-600">{d.h}</td>
                          <td className="px-4 py-3 text-center font-semibold text-yellow-500">{d.s}</td>
                          <td className="px-4 py-3 text-center font-semibold text-blue-500">{d.i}</td>
                          <td className="px-4 py-3 text-center font-semibold text-red-500">{d.a}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}